import {useState, useEffect} from "react"
import {useSelector} from "react-redux"
import ModalContainerModule from "../../modules/modalContainer"
import {ResponsiveRow} from "../../../constants/layout.constants"
import {Alert, Grid, InputLabel, TextField} from "@mui/material"
import {
    DetailsLabel,
    DetailsRow,
    DetailsText
} from "../../../constants/component.constants"
import NoteLogsCard from "../noteLogsCard"
import {formatDate, isEmptyString} from "../../../util/util"
import {processListingApplication, markApplicationAsViewed} from "../../../services/listingsServices/listing.service"
import {issueSuccessMessage, issueErrorMessage, issueWarnMessage} from "../../../actions/message"

export default function ViewApplication(props) {
    const [note, setNote] = useState("")
    const [isProcessing, setIsProcessing] = useState(false)
    const user = useSelector((state) => state.auth.user)
    // Force refresh to clear cache

    // Check if application can be approved/rejected
    // For SmartKodi clients: only pending applications with existing units (unitId present)
    // For non-SmartKodi clients: all pending applications
    // For "My Applications": never allow approve/reject (view-only)
    const isSmartKodiClient = user?.roles?.includes("BLDIR") || 
                             user?.roles?.includes("BLDMG") || 
                             user?.roles?.includes("CS")
    
    const hasExistingUnit = props.applicationItem.listing?.unitId && 
                           !isEmptyString(props.applicationItem.listing.unitId)
    
    const isMyApplication = props.isMyApplication || false // New prop to indicate if this is from "My Applications"
    
    const canApproveReject = !isMyApplication && props.applicationItem.statusCd === "PEN" && 
        (isSmartKodiClient ? hasExistingUnit : true)

    // Mark application as viewed when modal opens for non-existing SmartKodi units
    useEffect(() => {
        const alreadyViewed = !!props.applicationItem.viewedOn
        if (props.openViewModal && props.applicationItem.statusCd === "PEN" && 
            isSmartKodiClient && !hasExistingUnit && !isMyApplication && !alreadyViewed) {
            // Mark as viewed for non-existing SmartKodi units
            markApplicationAsViewed({
                applicationId: props.applicationItem.id
            }).then((response) => {
                if (response.type === "success") {
                    // Call the callback to update parent state with a small delay
                    if (props.onApplicationUpdated) {
                        setTimeout(() => {
                            props.onApplicationUpdated()
                        }, 100)
                    }
                    // Dispatch event to update navbar pending count
                    window.dispatchEvent(new Event('applicationStatusChanged'))
                }
            }).catch((error) => {
                console.error("Error marking application as viewed:", error)
                // Don't show error to user as this is not critical
            })
        }
    }, [props.openViewModal, props.applicationItem.id, props.applicationItem.statusCd, props.applicationItem.viewedOn, isSmartKodiClient, hasExistingUnit, isMyApplication])

    const handleApprove = async () => {
        if (!note.trim()) {
            issueWarnMessage("Please provide a reason for approval")
            return
        }

        setIsProcessing(true)
        try {
            const response = await processListingApplication({
                applicationId: props.applicationItem.id,
                action: "approve",
                reason: note.trim()
            })
            
            if (response.type === "success") {
                issueSuccessMessage("Application approved successfully!")
                props.closeViewModal()
                // Refresh the applications list if a callback is provided
                if (props.onApplicationUpdated) {
                    props.onApplicationUpdated()
                }
                // Dispatch event to update navbar pending count
                window.dispatchEvent(new Event('applicationStatusChanged'))
            } else {
                issueErrorMessage("Failed to approve application: " + response.message)
            }
        } catch (error) {
            console.error("Error approving application:", error)
            // Check if this is a subscription validation error
            if (error.message && error.message.includes("SmartKodi")) {
                issueErrorMessage(error.message)
                props.closeViewModal()
            } else {
                issueErrorMessage("Failed to approve application. Please try again.")
            }
        } finally {
            setIsProcessing(false)
        }
    }

    const handleReject = async () => {
        if (!note.trim()) {
            issueWarnMessage("Please provide a reason for rejection")
            return
        }

        setIsProcessing(true)
        try {
            const response = await processListingApplication({
                applicationId: props.applicationItem.id,
                action: "reject",
                reason: note.trim()
            })
            
            if (response.type === "success") {
                issueSuccessMessage("Application rejected successfully!")
                props.closeViewModal()
                // Refresh the applications list if a callback is provided
                if (props.onApplicationUpdated) {
                    props.onApplicationUpdated()
                }
                // Dispatch event to update navbar pending count
                window.dispatchEvent(new Event('applicationStatusChanged'))
            } else {
                issueErrorMessage("Failed to reject application: " + response.message)
            }
        } catch (error) {
            console.error("Error rejecting application:", error)
            // Check if this is a subscription validation error
            if (error.message && error.message.includes("SmartKodi")) {
                issueErrorMessage(error.message)
                props.closeViewModal()
            } else {
                issueErrorMessage("Failed to reject application. Please try again.")
            }
        } finally {
            setIsProcessing(false)
        }
    }

    const handleCancel = () => {
        if (!isProcessing) {
            props.closeViewModal()
        }
    }

    // Use the same modal for both pending and approved applications
    return (
        <ModalContainerModule
            size="md"
            accept={canApproveReject ? handleApprove : handleCancel}
            reject={canApproveReject ? handleReject : null}
            cancel={canApproveReject ? handleCancel : null}
            openModal={props.openViewModal}
            modalTitle={"Application for " + props.applicationItem.applicant.name}
            acceptButtonText={canApproveReject ? "Approve" : "Close"}
            rejectButtonText={canApproveReject ? "Reject" : ""}
            acceptButtonDisabled={isProcessing}
            rejectButtonDisabled={isProcessing}
        >
            <ResponsiveRow sx={{
                paddingTop: 0, 
                maxHeight: "70vh",
                overflow: "auto"
            }}>
                <Grid container spacing={3} sx={{
                    width: "100%",
                    minHeight: "400px"
                }}>
                    <Grid item xs={12} md={6} sx={{
                        minWidth: "300px"
                    }}>
                        <DetailsRow>
                            <DetailsLabel>Applicant:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {props.applicationItem.applicant.name}
                            </DetailsText>
                        </DetailsRow>
                        <DetailsRow>
                            <DetailsLabel>Listing:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {props.applicationItem.listing.propertyName}
                            </DetailsText>
                        </DetailsRow>
                        <DetailsRow>
                            <DetailsLabel>Status:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {props.applicationItem.status.name}
                            </DetailsText>
                        </DetailsRow>
                        <DetailsRow>
                            <DetailsLabel>Email:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {props.applicationItem.applicant.email || "Not provided"}
                            </DetailsText>
                        </DetailsRow>
                        <DetailsRow>
                            <DetailsLabel>Mobile Phone:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {props.applicationItem.applicant.mobilePhone || "Not provided"}
                            </DetailsText>
                        </DetailsRow>
                        {props.applicationItem.applicant.officePhone && (
                            <DetailsRow>
                                <DetailsLabel>Office Phone:</DetailsLabel>
                                <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                    {props.applicationItem.applicant.officePhone}
                                </DetailsText>
                            </DetailsRow>
                        )}
                        <DetailsRow>
                            <DetailsLabel>Application Date:</DetailsLabel>
                            <DetailsText sx={{ wordWrap: "break-word", overflowWrap: "break-word" }}>
                                {formatDate(props.applicationItem.dateCreated)}
                            </DetailsText>
                        </DetailsRow>

                        {/* Show unit existence warning for SmartKodi clients with non-existing units */}
                        {isSmartKodiClient && !hasExistingUnit && (
                            <Alert 
                                severity="warning" 
                                sx={{
                                    marginTop: "16px",
                                    fontSize: "0.875rem"
                                }}
                            >
                                View Only - Listing not on SmartKodi platform
                            </Alert>
                        )}

                        {/* Show processing info if available */}
                        {props.applicationItem.processingInfo && (
                            <>
                                <DetailsRow>
                                    <DetailsLabel>{props.applicationItem.processingInfo.action === 'approved' ? 'Approved' : 'Rejected'} On:</DetailsLabel>
                                    <DetailsText>
                                        {formatDate(props.applicationItem.processingInfo.processedOn)}
                                    </DetailsText>
                                </DetailsRow>
                                <DetailsRow>
                                    <DetailsLabel>{props.applicationItem.processingInfo.action === 'approved' ? 'Approved' : 'Rejected'} By:</DetailsLabel>
                                    <DetailsText>
                                        {props.applicationItem.processingInfo.processedBy}
                                    </DetailsText>
                                </DetailsRow>
                                <DetailsRow>
                                    <DetailsLabel>{props.applicationItem.processingInfo.action === 'approved' ? 'Approval' : 'Rejection'} Reason:</DetailsLabel>
                                    <DetailsText>
                                        {props.applicationItem.processingInfo.processReason}
                                    </DetailsText>
                                </DetailsRow>
                            </>
                        )}

                        {canApproveReject && (
                            <Alert severity="info">
                                Please give a reason for Approving/Rejecting this application
                            </Alert>
                        )}

                        {canApproveReject && (
                            <ResponsiveRow container sx={{height: "auto", marginTop: "16px"}}>
                                <Grid
                                    item
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        width: "100%",
                                    }}
                                >
                                    <InputLabel sx={{marginBottom: "8px"}}>Reason* ({500 - (note.length)})</InputLabel>
                                    <TextField
                                        sx={{
                                            width: "100%",
                                            "& .MuiInputBase-root": {
                                                minHeight: "100px",
                                                wordWrap: "break-word",
                                                overflowWrap: "break-word"
                                            }
                                        }}
                                        id={"note"}
                                        name={"note"}
                                        isrequired
                                        value={note}
                                        onChange={(event) => setNote(event.target.value)}
                                        multiline
                                        rows={4}
                                        disabled={isProcessing}
                                    />
                                </Grid>
                            </ResponsiveRow>
                        )}
                    </Grid>

                    <Grid item xs={12} md={6} sx={{
                        minWidth: "300px"
                    }}>
                        <NoteLogsCard 
                            notesMode={"ListingApplication"} 
                            contract={props.applicationItem} 
                            width={{ xs: "100%", lg: "100%" }} 
                            height="500px"
                        />
                    </Grid>
                </Grid>
            </ResponsiveRow>
        </ModalContainerModule>
    )
}